import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow, MaskLines } from "@/components/Reveal";
import { STORY } from "@/data/story";
import { IMAGES } from "@/data/content";

export default function StoryPage() {
  return (
    <main data-testid="story-page" className="bg-sand">
      <section className="grain relative flex min-h-[52svh] items-end overflow-hidden bg-obsidian">
        <img
          src={IMAGES.heroNature}
          alt="Misty mountain valley at sunrise in Honduras"
          className="absolute inset-0 h-full w-full scale-105 object-cover"
        />
        <div className="absolute inset-0 bg-obsidian/75" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/35 to-obsidian/70" />

        <div className="relative z-10 mx-auto w-full max-w-[1500px] px-6 pb-12 pt-32 lg:px-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.15 }}
          >
            <Link
              to="/"
              data-testid="story-back-link"
              className="label-eyebrow inline-flex items-center gap-2 text-sand/55 transition-colors hover:text-sand"
            >
              <ArrowLeft size={13} /> Back to home
            </Link>
          </motion.div>

          <h1 className="font-display mt-8 max-w-4xl text-[2.4rem] font-normal leading-[1.3] tracking-[-0.02em] text-sand sm:text-5xl lg:text-[4.6rem]">
            <MaskLines lines={["A Journey of Faith,"]} delay={0.3} lineClassName="pb-[0.22em]" />
            <span className="block overflow-hidden">
              <motion.span
                initial={{ y: "110%", opacity: 0 }}
                animate={{ y: "0%", opacity: 1 }}
                transition={{ duration: 1.15, delay: 0.42, ease: [0.22, 1, 0.36, 1] }}
                className="block pb-[0.26em] italic text-gold"
              >
                Hope, and Love
              </motion.span>
            </span>
          </h1>
        </div>
      </section>

      <section className="hairline-grid py-16 lg:py-20">
        <div className="mx-auto max-w-[1500px] px-6 lg:px-12">
          <Reveal>
            <figure data-testid="story-group-photo" className="mb-14 lg:mb-16">
              <div className="relative overflow-hidden">
                <img
                  src="/family/group.jpg"
                  alt="Todd Boyle with the four boys"
                  className="h-[16rem] w-full object-cover object-center sm:h-[24rem] lg:h-[34rem]"
                />
                <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-navy/10" />
              </div>
            </figure>
          </Reveal>

          {STORY.map((chapter, ci) => (
            <article
              key={chapter.no}
              data-testid={`story-chapter-${ci + 1}`}
              className={`grid gap-8 lg:grid-cols-12 lg:gap-20 ${
                ci > 0 ? "mt-5 border-t border-navy/12 pt-5 lg:mt-6 lg:pt-6" : ""
              }`}
            >
              <div className="lg:col-span-4">
                <Reveal>
                  <div className="lg:sticky lg:top-28">
                    <span className="font-display block text-5xl leading-none text-gold/70">
                      {chapter.no}
                    </span>
                    <h2 className="font-display mt-4 text-[1.6rem] leading-[1.15] tracking-[-0.01em] text-navy lg:text-[1.95rem]">
                      {chapter.title}
                    </h2>
                  </div>
                </Reveal>
              </div>

              <div className="lg:col-span-7 lg:col-start-6">
                {chapter.paragraphs.map((p, pi) => (
                  <Reveal key={pi} delay={0.06 * pi}>
                    <p
                      className={`text-base leading-[1.85] text-navy/75 ${
                        pi === 0 ? "drop-cap" : "mt-5"
                      }`}
                    >
                      {p}
                    </p>
                  </Reveal>
                ))}
              </div>
            </article>
          ))}

          <Reveal>
            <div className="mt-16 border-l-2 border-gold pl-7">
              <p className="font-display text-2xl italic leading-relaxed text-navy lg:text-3xl">
                “And now these three remain: faith, hope and love. But the greatest of these is
                love.”
              </p>
              <p className="label-eyebrow mt-5 text-sage">1 Corinthians 13:13</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative overflow-hidden bg-navy py-14 lg:py-16">
        <div className="mx-auto flex max-w-[1500px] flex-col gap-8 px-6 lg:flex-row lg:items-end lg:justify-between lg:px-12">
          <Reveal>
            <Eyebrow tone="gold">Walk With Us</Eyebrow>
            <p className="font-display mt-6 max-w-2xl text-[1.8rem] leading-[1.15] text-sand lg:text-4xl">
              The circumstances have changed.
              <span className="italic text-gold"> The calling remains.</span>
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/#give"
                data-testid="story-give-btn"
                className="btn-sheen inline-flex items-center gap-2 rounded-full bg-crimson px-8 py-4 font-mono-accent text-[0.7rem] uppercase tracking-[0.2em] text-sand"
              >
                Give Now <ArrowRight size={15} />
              </Link>
              <Link
                to="/#contact"
                data-testid="story-contact-btn"
                className="btn-sheen inline-flex items-center gap-2 rounded-full border border-sand/30 px-8 py-4 font-mono-accent text-[0.7rem] uppercase tracking-[0.2em] text-sand hover:bg-sand hover:text-navy"
              >
                Get In Touch
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
